import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import {
  Send,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  Building2,
  User,
  Calendar,
  FileText,
  MapPin,
  AlertCircle,
  Mail,
  MessageCircle,
} from "lucide-react";
import { ExportDropdown } from "../components/ui/export-dropdown";
import { MessageModal } from "../components/MessageModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Get user from localStorage
const getUser = () => {
  const userStr = localStorage.getItem("user");
  return userStr ? JSON.parse(userStr) : null;
};

export default function PlacementRequests() {
  const user = getUser();
  const [requests, setRequests] = useState([]);
  const [students, setStudents] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    student_id: "",
    clinical_site_id: "",
    requested_start_date: "",
    requested_end_date: "",
    rotation_type: "",
    rotation_type_dates: "",
    clinical_requirements: "",
    request_notes: "",
  });
  const [counterData, setCounterData] = useState({
    alternative_site_id: "",
    proposed_start_date: "",
    proposed_end_date: "",
    rotation_type_key: "",
    original_rotation_key: "",
    counter_notes: "",
  });
  const [denyReason, setDenyReason] = useState("");
  const [managedSites, setManagedSites] = useState([]);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedMessageRequest, setSelectedMessageRequest] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [requestsRes, studentsRes, sitesRes] = await Promise.all([
        api.get("/placement-requests"),
        api.get("/students"),
        api.get("/clinical-sites"),
      ]);
      setRequests(requestsRes.data);
      setStudents(studentsRes.data);
      setSites(sitesRes.data);
      
      // Fetch unread message counts for all requests
      const counts = {};
      for (const req of requestsRes.data) {
        try {
          const countRes = await api.get(`/placement-requests/${req.id}/messages/unread-count`);
          counts[req.id] = countRes.data.unread_count;
        } catch {
          counts[req.id] = 0;
        }
      }
      setUnreadCounts(counts);
      
      // For clinical site users, set all sites as available for counter offers
      // This allows them to offer alternative sites or propose different dates for the same site
      if (user?.role === "clinical_site") {
        setManagedSites(sitesRes.data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post("/placement-requests", formData);
      toast.success("Placement request submitted successfully!");
      setDialogOpen(false);
      setFormData({
        student_id: "",
        clinical_site_id: "",
        requested_start_date: "",
        requested_end_date: "",
        rotation_type: "",
        rotation_type_dates: "",
        clinical_requirements: "",
        request_notes: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const handleAccept = async (requestId) => {
    try {
      await api.put(`/placement-requests/${requestId}/accept`);
      toast.success("Request accepted! Placement confirmed.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept request");
    }
  };

  const handleCounter = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;
    try {
      await api.put(`/placement-requests/${selectedRequest.id}/counter`, counterData);
      toast.success("Counter offer submitted!");
      setCounterDialogOpen(false);
      setSelectedRequest(null);
      setCounterData({
        alternative_site_id: "",
        proposed_start_date: "",
        proposed_end_date: "",
        rotation_type_key: "",
        original_rotation_key: "",
        counter_notes: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit counter offer");
    }
  };

  const handleDeny = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;
    try {
      await api.put(`/placement-requests/${selectedRequest.id}/deny`, { denial_reason: denyReason });
      toast.success("Request denied");
      setDenyDialogOpen(false);
      setSelectedRequest(null);
      setDenyReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deny request");
    }
  };

  const handleAcceptCounter = async (requestId) => {
    try {
      await api.put(`/placement-requests/${requestId}/accept-counter`);
      toast.success("Counter offer accepted! Placement confirmed.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept counter offer");
    }
  };

  const handleDeclineCounter = async (requestId) => {
    try {
      await api.put(`/placement-requests/${requestId}/decline-counter`);
      toast.success("Counter offer declined");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline counter offer");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-400/10 text-yellow-400 border-yellow-400/20", icon: Clock },
      accepted: { color: "bg-green-400/10 text-green-400 border-green-400/20", icon: CheckCircle },
      countered: { color: "bg-blue-400/10 text-blue-400 border-blue-400/20", icon: RefreshCw },
      denied: { color: "bg-red-400/10 text-red-400 border-red-400/20", icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const pendingCount = requests.filter(r => r.status === "pending").length;
  const acceptedCount = requests.filter(r => r.status === "accepted").length;
  const counteredCount = requests.filter(r => r.status === "countered").length;
  const deniedCount = requests.filter(r => r.status === "denied").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Placement Requests</h1>
          <p className="text-slate-400 mt-1">
            {user?.role === "university" 
              ? "Submit and track placement requests to clinical sites"
              : "Manage incoming placement requests from universities"
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExportDropdown 
            endpoint="/export/placement-requests" 
            filename="placement_requests"
          />
          {user?.role === "university" && (
            <Button 
              onClick={() => setDialogOpen(true)}
              className="bg-cyan-400 text-black hover:bg-cyan-300"
              data-testid="new-request-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              New Request
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-yellow-400/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Pending</p>
              <p className="text-2xl font-bold text-slate-100">{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-400/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Accepted</p>
              <p className="text-2xl font-bold text-slate-100">{acceptedCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-400/10 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Countered</p>
              <p className="text-2xl font-bold text-slate-100">{counteredCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-red-400/10 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Denied</p>
              <p className="text-2xl font-bold text-slate-100">{deniedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Helper function to get site address */}
      {(() => {
        const getSiteAddress = (siteId) => {
          const site = sites.find(s => s.id === siteId);
          if (site) {
            return `${site.city}, ${site.state}`;
          }
          return "";
        };
        
        return null;
      })()}

      {/* Requests List */}
      <div className="space-y-4">
        {requests.length === 0 ? (
          <Card className="bg-[#151A23] border-slate-700">
            <CardContent className="py-16 text-center">
              <Send className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-300 mb-2">No requests yet</h3>
              <p className="text-slate-500">
                {user?.role === "university" 
                  ? "Submit your first placement request to get started"
                  : "No incoming requests at this time"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          [...requests]
            .sort((a, b) => {
              // Status priority: pending/accepted/countered first, then confirmed/denied
              const statusPriority = {
                'pending': 1,
                'accepted': 2,
                'countered': 3,
                'confirmed': 4,
                'denied': 5
              };
              const priorityA = statusPriority[a.status] || 99;
              const priorityB = statusPriority[b.status] || 99;
              
              if (priorityA !== priorityB) {
                return priorityA - priorityB;
              }
              // Within same status, sort by date (newest first)
              return new Date(b.created_at) - new Date(a.created_at);
            })
            .map((request) => {
            const siteAddress = sites.find(s => s.id === request.clinical_site_id);
            const addressText = siteAddress ? `${siteAddress.address}, ${siteAddress.city}, ${siteAddress.state}` : "";
            const student = students.find(s => s.id === request.student_id);
            const site = sites.find(s => s.id === request.clinical_site_id);
            
            return (
            <Card key={request.id} className="bg-[#151A23] border-slate-700" data-testid={`request-card-${request.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">
                        {request.short_id || 'RQ----'}
                      </span>
                      {getStatusBadge(request.status)}
                      <span className="text-slate-500 text-sm">
                        Submitted {formatDate(request.created_at?.split("T")[0])}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-cyan-400 mt-0.5" />
                        <div>
                          <p className="text-slate-400 text-sm">Student</p>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">
                              {student?.short_id || 'S----'}
                            </span>
                            <p className="text-slate-100 font-medium">{request.student_name}</p>
                          </div>
                          {request.student_program && (
                            <Badge className="mt-1 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                              {request.student_program}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Building2 className="w-5 h-5 text-cyan-400 mt-0.5" />
                        <div>
                          <p className="text-slate-400 text-sm">Clinical Site</p>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">
                              {site?.short_id || 'CS----'}
                            </span>
                            <p className="text-slate-100 font-medium">{request.clinical_site_name}</p>
                          </div>
                          {addressText && (
                            <p className="text-slate-400 text-xs mt-0.5">{addressText}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-cyan-400 mt-0.5" />
                        <div>
                          <p className="text-slate-400 text-sm">Requested Dates</p>
                          <p className="text-slate-100 font-medium">
                            {formatDate(request.requested_start_date)} - {formatDate(request.requested_end_date)}
                          </p>
                        </div>
                      </div>
                      
                      {request.rotation_type && (
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-cyan-400 mt-0.5" />
                          <div>
                            <p className="text-slate-400 text-sm">Rotation Type</p>
                            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                              {request.rotation_type}
                            </Badge>
                          </div>
                        </div>
                      )}
                      
                      {/* Submitted By Section */}
                      <div className="flex items-start gap-3 col-span-2 mt-2 p-3 bg-slate-800/30 border border-slate-700/50 rounded-lg">
                        <User className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-slate-400 text-sm mb-1">Submitted By</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <p className="text-slate-100 font-medium">
                                {request.submitter_first_name} {request.submitter_last_name}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5 text-slate-500" />
                              <p className="text-slate-300 text-sm">{request.submitter_email || 'N/A'}</p>
                            </div>
                            {request.submitter_campus && (
                              <div className="flex items-center gap-1 col-span-2">
                                <MapPin className="w-3.5 h-3.5 text-slate-500" />
                                <p className="text-slate-300 text-sm">Campus: {request.submitter_campus}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {request.clinical_requirements && (
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-cyan-400 mt-0.5" />
                          <div>
                            <p className="text-slate-400 text-sm">Requirements</p>
                            <p className="text-slate-300 text-sm">{request.clinical_requirements}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Counter Offer Details */}
                    {request.status === "countered" && request.counter_offer && (() => {
                      const altSite = sites.find(s => s.id === request.counter_offer.alternative_site_id);
                      const altAddress = altSite ? `${altSite.address}, ${altSite.city}, ${altSite.state}` : "";
                      
                      return (
                      <div className="mt-4 p-4 bg-blue-400/5 border border-blue-400/20 rounded-lg">
                        <h4 className="text-blue-400 font-medium mb-3 flex items-center gap-2">
                          <RefreshCw className="w-4 h-4" />
                          Counter Offer Received
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Proposed Site</p>
                            <p className="text-slate-100 font-medium">
                              {request.counter_offer.alternative_site_name || "Same Site"}
                              {request.counter_offer.alternative_site_id === request.clinical_site_id && (
                                <span className="text-slate-500 font-normal ml-1">(unchanged)</span>
                              )}
                            </p>
                            {altAddress && (
                              <p className="text-slate-400 text-xs mt-0.5">{altAddress}</p>
                            )}
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Proposed Dates</p>
                            <p className="text-slate-100 font-medium">
                              {formatDate(request.counter_offer.proposed_start_date)} - {formatDate(request.counter_offer.proposed_end_date)}
                            </p>
                            {(request.counter_offer.proposed_start_date !== request.requested_start_date ||
                              request.counter_offer.proposed_end_date !== request.requested_end_date) && (
                              <p className="text-yellow-400 text-xs mt-1">
                                (Original: {formatDate(request.requested_start_date)} - {formatDate(request.requested_end_date)})
                              </p>
                            )}
                          </div>
                          {request.counter_offer.counter_notes && (
                            <div className="col-span-2 mt-2 p-3 bg-slate-800/50 rounded">
                              <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Reason</p>
                              <p className="text-slate-300">{request.counter_offer.counter_notes}</p>
                            </div>
                          )}
                        </div>
                        {user?.role === "university" && (
                          <p className="text-blue-400/70 text-xs mt-3 italic">
                            Review the counter offer above and accept or decline using the buttons on the right.
                          </p>
                        )}
                      </div>
                      );
                    })()}

                    {/* Denial Reason */}
                    {request.status === "denied" && request.denial_reason && (
                      <div className="mt-4 p-4 bg-red-400/5 border border-red-400/20 rounded-lg">
                        <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Denial Reason
                        </h4>
                        <p className="text-slate-300 text-sm">{request.denial_reason}</p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Clinical Site Actions */}
                    {user?.role === "clinical_site" && request.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleAccept(request.id)}
                          className="bg-green-500 hover:bg-green-400 text-white"
                          data-testid={`accept-btn-${request.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedRequest(request);
                            // Find matching rotation key from original dates
                            const rotationOptions = {
                              "TCE1_JAN_APR_2027": { start: "2027-01-11", end: "2027-04-02" },
                              "TCE2_JAN_APR_2027": { start: "2027-01-11", end: "2027-04-02" },
                              "TC1_JAN_APR_2027": { start: "2027-01-25", end: "2027-04-16" },
                              "TC2_JAN_MAR_2027": { start: "2027-01-11", end: "2027-03-19" },
                              "TC3_JAN_MAR_2027": { start: "2027-01-11", end: "2027-03-19" },
                              "TCE1_MAY_AUG_2027": { start: "2027-05-10", end: "2027-08-03" },
                              "TCE2_MAY_AUG_2027": { start: "2027-05-10", end: "2027-08-03" },
                              "TC1_MAY_AUG_2027": { start: "2027-05-24", end: "2027-08-13" },
                              "TC2_MAY_JUL_2027": { start: "2027-05-10", end: "2027-07-16" },
                              "TC3_MAY_JUL_2027": { start: "2027-05-10", end: "2027-07-16" },
                              "TCE1_SEP_DEC_2027": { start: "2027-09-07", end: "2027-12-03" },
                              "TCE2_SEP_DEC_2027": { start: "2027-09-07", end: "2027-12-03" },
                              "TC1_SEP_DEC_2027": { start: "2027-09-20", end: "2027-12-10" },
                              "TC2_SEP_NOV_2027": { start: "2027-09-07", end: "2027-11-12" },
                              "TC3_SEP_NOV_2027": { start: "2027-09-07", end: "2027-11-12" },
                            };
                            let matchingKey = "";
                            for (const [key, dates] of Object.entries(rotationOptions)) {
                              if (dates.start === request.requested_start_date && dates.end === request.requested_end_date) {
                                matchingKey = key;
                                break;
                              }
                            }
                            // Pre-fill with original request data
                            setCounterData({
                              alternative_site_id: request.clinical_site_id,
                              proposed_start_date: request.requested_start_date,
                              proposed_end_date: request.requested_end_date,
                              rotation_type_key: matchingKey,
                              original_rotation_key: matchingKey,
                              counter_notes: "",
                            });
                            setCounterDialogOpen(true);
                          }}
                          className="border-blue-400 text-blue-400 hover:bg-blue-400/10"
                          data-testid={`counter-btn-${request.id}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Counter
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedRequest(request);
                            setDenyDialogOpen(true);
                          }}
                          className="border-red-400 text-red-400 hover:bg-red-400/10"
                          data-testid={`deny-btn-${request.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Deny
                        </Button>
                      </>
                    )}

                    {/* University Actions on Counter */}
                    {user?.role === "university" && request.status === "countered" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleAcceptCounter(request.id)}
                          className="bg-green-500 hover:bg-green-400 text-white"
                          data-testid={`accept-counter-btn-${request.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept Counter
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeclineCounter(request.id)}
                          className="border-red-400 text-red-400 hover:bg-red-400/10"
                          data-testid={`decline-counter-btn-${request.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Decline
                        </Button>
                      </>
                    )}

                    {/* Message Button - Available for all roles */}
                    {(user?.role === "university" || user?.role === "clinical_site") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedMessageRequest(request);
                          setMessageModalOpen(true);
                        }}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700/50 relative"
                        data-testid={`message-btn-${request.id}`}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        Messages
                        {unreadCounts[request.id] > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-cyan-500 text-black text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadCounts[request.id]}
                          </span>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })
        )}
      </div>

      {/* New Request Dialog (University) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Submit Placement Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitRequest} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Student</Label>
              <SearchableSelect
                options={students.filter(s => !s.placement_status).map((student) => ({
                  value: student.id,
                  label: `${student.first_name} ${student.last_name}`,
                  description: student.program,
                }))}
                value={formData.student_id}
                onValueChange={(value) => setFormData({ ...formData, student_id: value })}
                placeholder="Search and select student..."
                searchPlaceholder="Type student name..."
                emptyText="No matching students found."
              />
            </div>

            <div>
              <Label className="text-slate-300">Clinical Site</Label>
              <SearchableSelect
                options={sites.map((site) => ({
                  value: site.id,
                  label: site.name,
                  description: `${site.city}, ${site.state}`,
                }))}
                value={formData.clinical_site_id}
                onValueChange={(value) => setFormData({ ...formData, clinical_site_id: value })}
                placeholder="Search and select clinical site..."
                searchPlaceholder="Type site name or city..."
                emptyText="No matching sites found."
              />
              <p className="text-xs text-slate-500 mt-1">
                You can request placement regardless of slot availability
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Rotation Type and Dates</Label>
              <Select 
                value={formData.rotation_type_dates} 
                onValueChange={(value) => {
                  // Parse the selected value to extract rotation type and dates
                  const rotationOptions = {
                    "TCE1_JAN_APR_2027": { type: "TCE 1", start: "2027-01-11", end: "2027-04-02", label: "TCE 1 – 12 weeks Jan 11 – Apr 2, 2027" },
                    "TCE2_JAN_APR_2027": { type: "TCE 2", start: "2027-01-11", end: "2027-04-02", label: "TCE 2 – 12 weeks Jan 11 – Apr 2, 2027" },
                    "TC1_JAN_APR_2027": { type: "TC I", start: "2027-01-25", end: "2027-04-16", label: "TC I – 12 weeks Jan 25 – Apr 16, 2027" },
                    "TC2_JAN_MAR_2027": { type: "TC II", start: "2027-01-11", end: "2027-03-19", label: "TC II – 10 weeks Jan 11 – Mar 19, 2027" },
                    "TC3_JAN_MAR_2027": { type: "TC III", start: "2027-01-11", end: "2027-03-19", label: "TC III – 10 weeks Jan 11 – Mar 19, 2027" },
                    "TCE1_MAY_AUG_2027": { type: "TCE 1", start: "2027-05-10", end: "2027-08-03", label: "TCE 1 – 12 weeks May 10 – Aug 3, 2027" },
                    "TCE2_MAY_AUG_2027": { type: "TCE 2", start: "2027-05-10", end: "2027-08-03", label: "TCE 2 – 12 weeks May 10 – Aug 3, 2027" },
                    "TC1_MAY_AUG_2027": { type: "TC I", start: "2027-05-24", end: "2027-08-13", label: "TC I – 12 weeks May 24 – Aug 13, 2027" },
                    "TC2_MAY_JUL_2027": { type: "TC II", start: "2027-05-10", end: "2027-07-16", label: "TC II – 10 weeks May 10 – Jul 16, 2027" },
                    "TC3_MAY_JUL_2027": { type: "TC III", start: "2027-05-10", end: "2027-07-16", label: "TC III – 10 weeks May 10 – Jul 16, 2027" },
                    "TCE1_SEP_DEC_2027": { type: "TCE 1", start: "2027-09-07", end: "2027-12-03", label: "TCE 1 – 12 weeks Sep 7 – Dec 3, 2027" },
                    "TCE2_SEP_DEC_2027": { type: "TCE 2", start: "2027-09-07", end: "2027-12-03", label: "TCE 2 – 12 weeks Sep 7 – Dec 3, 2027" },
                    "TC1_SEP_DEC_2027": { type: "TC I", start: "2027-09-20", end: "2027-12-10", label: "TC I – 12 weeks Sep 20 – Dec 10, 2027" },
                    "TC2_SEP_NOV_2027": { type: "TC II", start: "2027-09-07", end: "2027-11-12", label: "TC II – 10 weeks Sep 7 – Nov 12, 2027" },
                    "TC3_SEP_NOV_2027": { type: "TC III", start: "2027-09-07", end: "2027-11-12", label: "TC III – 10 weeks Sep 7 – Nov 12, 2027" },
                  };
                  const selected = rotationOptions[value];
                  if (selected) {
                    setFormData({ 
                      ...formData, 
                      rotation_type_dates: value,
                      rotation_type: selected.label,
                      requested_start_date: selected.start,
                      requested_end_date: selected.end
                    });
                  }
                }}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200">
                  <SelectValue placeholder="Select rotation type and dates..." />
                </SelectTrigger>
                <SelectContent className="bg-[#151A23] border-slate-700 max-h-[300px]">
                  {/* Spring 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Spring 2027</div>
                  <SelectItem value="TCE1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks Jan 11 – Apr 2, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks Jan 11 – Apr 2, 2027
                  </SelectItem>
                  <SelectItem value="TC1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks Jan 25 – Apr 16, 2027
                  </SelectItem>
                  <SelectItem value="TC2_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks Jan 11 – Mar 19, 2027
                  </SelectItem>
                  <SelectItem value="TC3_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks Jan 11 – Mar 19, 2027
                  </SelectItem>
                  {/* Summer 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Summer 2027</div>
                  <SelectItem value="TCE1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks May 10 – Aug 3, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks May 10 – Aug 3, 2027
                  </SelectItem>
                  <SelectItem value="TC1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks May 24 – Aug 13, 2027
                  </SelectItem>
                  <SelectItem value="TC2_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks May 10 – Jul 16, 2027
                  </SelectItem>
                  <SelectItem value="TC3_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks May 10 – Jul 16, 2027
                  </SelectItem>
                  {/* Fall 2027 */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Fall 2027</div>
                  <SelectItem value="TCE1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 1 – 12 weeks Sep 7 – Dec 3, 2027
                  </SelectItem>
                  <SelectItem value="TCE2_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TCE 2 – 12 weeks Sep 7 – Dec 3, 2027
                  </SelectItem>
                  <SelectItem value="TC1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800">
                    TC I – 12 weeks Sep 20 – Dec 10, 2027
                  </SelectItem>
                  <SelectItem value="TC2_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800">
                    TC II – 10 weeks Sep 7 – Nov 12, 2027
                  </SelectItem>
                  <SelectItem value="TC3_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800">
                    TC III – 10 weeks Sep 7 – Nov 12, 2027
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Clinical Requirements (Optional)</Label>
              <Textarea
                value={formData.clinical_requirements}
                onChange={(e) => setFormData({ ...formData, clinical_requirements: e.target.value })}
                placeholder="Specify any clinical requirements or prerequisites..."
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                rows={2}
              />
            </div>

            <div>
              <Label className="text-slate-300">Additional Notes (Optional)</Label>
              <Textarea
                value={formData.request_notes}
                onChange={(e) => setFormData({ ...formData, request_notes: e.target.value })}
                placeholder="Any additional information for the clinical site..."
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-400 text-black hover:bg-cyan-300">
                <Send className="w-4 h-4 mr-2" />
                Submit Request
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Counter Offer Dialog (Clinical Site) */}
      <Dialog open={counterDialogOpen} onOpenChange={setCounterDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 w-[95vw] max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Submit Counter Offer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCounter} className="space-y-3 mt-2 overflow-y-auto max-h-[70vh]">
            {/* Original Request Summary */}
            <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
              <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-2">Original Request</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Student:</span>
                  <span className="text-slate-200">{selectedRequest?.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Site:</span>
                  <span className="text-slate-200 text-right text-xs">{selectedRequest?.clinical_site_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Dates:</span>
                  <span className="text-slate-200">
                    {formatDate(selectedRequest?.requested_start_date)} - {formatDate(selectedRequest?.requested_end_date)}
                  </span>
                </div>
              </div>
            </div>

            {/* Counter Offer Form */}
            <div className="p-3 bg-blue-400/5 border border-blue-400/20 rounded-lg space-y-3">
              <h4 className="text-blue-400 text-xs uppercase tracking-wide flex items-center gap-2">
                <RefreshCw className="w-3 h-3" />
                Your Counter Offer
              </h4>
              
              <div>
                <Label className="text-slate-300 text-sm">Alternative Site</Label>
                <Select 
                  value={counterData.alternative_site_id} 
                  onValueChange={(value) => setCounterData({ ...counterData, alternative_site_id: value })}
                >
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200 text-sm">
                    <SelectValue placeholder="Select alternative site" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-slate-700 max-h-48 z-[100]" position="popper" sideOffset={4}>
                    {managedSites.map((site) => (
                      <SelectItem key={site.id} value={site.id} className="text-slate-200 focus:bg-slate-800 text-sm">
                        {site.name}
                        {site.id === selectedRequest?.clinical_site_id && " (Original)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Select same site if only changing rotation
                </p>
              </div>

              <div>
                <Label className="text-slate-300 text-sm">Alternative Rotation Type and Dates</Label>
                <Select 
                  value={counterData.rotation_type_key} 
                  onValueChange={(value) => {
                    const rotationOptions = {
                      "TCE1_JAN_APR_2027": { type: "TCE 1", start: "2027-01-11", end: "2027-04-02", label: "TCE 1 – 12 weeks Jan 11 – Apr 2, 2027" },
                      "TCE2_JAN_APR_2027": { type: "TCE 2", start: "2027-01-11", end: "2027-04-02", label: "TCE 2 – 12 weeks Jan 11 – Apr 2, 2027" },
                      "TC1_JAN_APR_2027": { type: "TC I", start: "2027-01-25", end: "2027-04-16", label: "TC I – 12 weeks Jan 25 – Apr 16, 2027" },
                      "TC2_JAN_MAR_2027": { type: "TC II", start: "2027-01-11", end: "2027-03-19", label: "TC II – 10 weeks Jan 11 – Mar 19, 2027" },
                      "TC3_JAN_MAR_2027": { type: "TC III", start: "2027-01-11", end: "2027-03-19", label: "TC III – 10 weeks Jan 11 – Mar 19, 2027" },
                      "TCE1_MAY_AUG_2027": { type: "TCE 1", start: "2027-05-10", end: "2027-08-03", label: "TCE 1 – 12 weeks May 10 – Aug 3, 2027" },
                      "TCE2_MAY_AUG_2027": { type: "TCE 2", start: "2027-05-10", end: "2027-08-03", label: "TCE 2 – 12 weeks May 10 – Aug 3, 2027" },
                      "TC1_MAY_AUG_2027": { type: "TC I", start: "2027-05-24", end: "2027-08-13", label: "TC I – 12 weeks May 24 – Aug 13, 2027" },
                      "TC2_MAY_JUL_2027": { type: "TC II", start: "2027-05-10", end: "2027-07-16", label: "TC II – 10 weeks May 10 – Jul 16, 2027" },
                      "TC3_MAY_JUL_2027": { type: "TC III", start: "2027-05-10", end: "2027-07-16", label: "TC III – 10 weeks May 10 – Jul 16, 2027" },
                      "TCE1_SEP_DEC_2027": { type: "TCE 1", start: "2027-09-07", end: "2027-12-03", label: "TCE 1 – 12 weeks Sep 7 – Dec 3, 2027" },
                      "TCE2_SEP_DEC_2027": { type: "TCE 2", start: "2027-09-07", end: "2027-12-03", label: "TCE 2 – 12 weeks Sep 7 – Dec 3, 2027" },
                      "TC1_SEP_DEC_2027": { type: "TC I", start: "2027-09-20", end: "2027-12-10", label: "TC I – 12 weeks Sep 20 – Dec 10, 2027" },
                      "TC2_SEP_NOV_2027": { type: "TC II", start: "2027-09-07", end: "2027-11-12", label: "TC II – 10 weeks Sep 7 – Nov 12, 2027" },
                      "TC3_SEP_NOV_2027": { type: "TC III", start: "2027-09-07", end: "2027-11-12", label: "TC III – 10 weeks Sep 7 – Nov 12, 2027" },
                    };
                    const selected = rotationOptions[value];
                    if (selected) {
                      setCounterData({ 
                        ...counterData, 
                        rotation_type_key: value,
                        proposed_start_date: selected.start,
                        proposed_end_date: selected.end
                      });
                    }
                  }}
                >
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-slate-200 text-sm">
                    <SelectValue placeholder="Select rotation type and dates..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151A23] border-slate-700 max-h-[250px] z-[100]" position="popper" sideOffset={4}>
                    {/* Spring 2027 */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Spring 2027</div>
                    <SelectItem value="TCE1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TCE 1 – 12 weeks Jan 11 – Apr 2, 2027{counterData.original_rotation_key === "TCE1_JAN_APR_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TCE2_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TCE 2 – 12 weeks Jan 11 – Apr 2, 2027{counterData.original_rotation_key === "TCE2_JAN_APR_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC1_JAN_APR_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC I – 12 weeks Jan 25 – Apr 16, 2027{counterData.original_rotation_key === "TC1_JAN_APR_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC2_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC II – 10 weeks Jan 11 – Mar 19, 2027{counterData.original_rotation_key === "TC2_JAN_MAR_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC3_JAN_MAR_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC III – 10 weeks Jan 11 – Mar 19, 2027{counterData.original_rotation_key === "TC3_JAN_MAR_2027" && " (Original)"}
                    </SelectItem>
                    {/* Summer 2027 */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Summer 2027</div>
                    <SelectItem value="TCE1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TCE 1 – 12 weeks May 10 – Aug 3, 2027{counterData.original_rotation_key === "TCE1_MAY_AUG_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TCE2_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TCE 2 – 12 weeks May 10 – Aug 3, 2027{counterData.original_rotation_key === "TCE2_MAY_AUG_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC1_MAY_AUG_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC I – 12 weeks May 24 – Aug 13, 2027{counterData.original_rotation_key === "TC1_MAY_AUG_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC2_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC II – 10 weeks May 10 – Jul 16, 2027{counterData.original_rotation_key === "TC2_MAY_JUL_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC3_MAY_JUL_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC III – 10 weeks May 10 – Jul 16, 2027{counterData.original_rotation_key === "TC3_MAY_JUL_2027" && " (Original)"}
                    </SelectItem>
                    {/* Fall 2027 */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400 bg-slate-800/50">Fall 2027</div>
                    <SelectItem value="TCE1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TCE 1 – 12 weeks Sep 7 – Dec 3, 2027{counterData.original_rotation_key === "TCE1_SEP_DEC_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TCE2_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TCE 2 – 12 weeks Sep 7 – Dec 3, 2027{counterData.original_rotation_key === "TCE2_SEP_DEC_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC1_SEP_DEC_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC I – 12 weeks Sep 20 – Dec 10, 2027{counterData.original_rotation_key === "TC1_SEP_DEC_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC2_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC II – 10 weeks Sep 7 – Nov 12, 2027{counterData.original_rotation_key === "TC2_SEP_NOV_2027" && " (Original)"}
                    </SelectItem>
                    <SelectItem value="TC3_SEP_NOV_2027" className="text-slate-200 focus:bg-slate-800 text-sm">
                      TC III – 10 weeks Sep 7 – Nov 12, 2027{counterData.original_rotation_key === "TC3_SEP_NOV_2027" && " (Original)"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300 text-sm">Reason (Optional)</Label>
                <Textarea
                  value={counterData.counter_notes}
                  onChange={(e) => setCounterData({ ...counterData, counter_notes: e.target.value })}
                  placeholder="Explain why you're proposing this alternative..."
                  className="bg-slate-800/50 border-slate-700 text-slate-200 text-sm"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" size="sm" onClick={() => setCounterDialogOpen(false)} className="text-slate-300">
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-500 text-white hover:bg-blue-400">
                <RefreshCw className="w-4 h-4 mr-1" />
                Submit Counter
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Deny Request Dialog (Clinical Site) */}
      <Dialog open={denyDialogOpen} onOpenChange={setDenyDialogOpen}>
        <DialogContent className="bg-[#151A23] border-slate-700 w-[95vw] max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Deny Placement Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDeny} className="space-y-4 mt-4">
            <div className="p-3 bg-red-400/5 border border-red-400/20 rounded-lg">
              <p className="text-slate-400 text-sm">
                Request from: <span className="text-slate-200">{selectedRequest?.student_name}</span>
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Reason for Denial <span className="text-red-400">*</span></Label>
              <Textarea
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                placeholder="Please provide a reason for denying this request..."
                className="bg-slate-800/50 border-slate-700 text-slate-200"
                rows={4}
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setDenyDialogOpen(false)} className="text-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-red-500 text-white hover:bg-red-400">
                <XCircle className="w-4 h-4 mr-2" />
                Deny Request
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Message Modal */}
      <MessageModal
        isOpen={messageModalOpen}
        onClose={() => {
          setMessageModalOpen(false);
          setSelectedMessageRequest(null);
          // Refresh unread counts after closing
          fetchData();
        }}
        requestId={selectedMessageRequest?.id}
        studentName={selectedMessageRequest?.student_name}
        clinicalSiteName={selectedMessageRequest?.clinical_site_name}
        requestShortId={selectedMessageRequest?.short_id}
      />
    </div>
  );
}
